import { useMemo } from 'react'
import { GetLiveMonitorReadingsSubscription } from '../../../generated/graphql'
import {
  DeviceHealth,
  deriveDeviceHealth,
  healthRank,
  readDeviceStatus,
} from '../util/deviceStatus'
import useLiveCallMonitorCallStates from './useLiveCallMonitorCallStates'
import useLiveCallMonitorParticipants, {
  SignedUpReader,
} from './useLiveCallMonitorParticipants'
import useLiveCallMonitorReadings from './useLiveCallMonitorReadings'
import useNow from './useNow'

const HEALTH_REFRESH_MS = 15 * 1000

export type MonitoredParticipant = ReturnType<
  typeof useLiveCallMonitorCallStates
>['callStatesByReadingId'][string][number] & {
  isPresent: boolean
  health: DeviceHealth
}

export type MonitoredReading =
  GetLiveMonitorReadingsSubscription['reading'][0] & {
    liveParticipants: MonitoredParticipant[]
    signedUpReaders: SignedUpReader[]
  }

export type UseLiveCallMonitorDataConfig = { skip?: boolean }

export default function useLiveCallMonitorData({
  skip = false,
}: UseLiveCallMonitorDataConfig = {}) {
  const {
    readings,
    loading: readingsLoading,
    error: readingsError,
  } = useLiveCallMonitorReadings({ skip })

  const readingIds = useMemo(() => readings.map(({ id }) => id), [readings])

  const { callStatesByReadingId, loading: callStatesLoading } =
    useLiveCallMonitorCallStates({ readingIds, skip })

  const {
    presenceByReadingId,
    signedUpByReadingId,
    loading: presenceLoading,
  } = useLiveCallMonitorParticipants({ readingIds, skip })

  const now = useNow(HEALTH_REFRESH_MS, skip)

  const monitoredReadings = useMemo<MonitoredReading[]>(
    () =>
      readings.map((reading) => {
        const presence = presenceByReadingId[reading.id] || {}

        const liveParticipants: MonitoredParticipant[] = (
          callStatesByReadingId[reading.id] || []
        )
          .map((callState) => {
            // participants not covered by reader/book derivation (e.g. staff observers) are
            // in live_call_state at all only while actually connected, so default to present.
            const isPresent = presence[callState.user_id]?.isPresent ?? true

            return {
              ...callState,
              isPresent,
              health: deriveDeviceHealth(readDeviceStatus(callState), {
                now,
                isPresent,
              }),
            }
          })
          .sort(
            (a, b) =>
              healthRank(b.health.level) - healthRank(a.health.level) ||
              Number(b.role === 'book') - Number(a.role === 'book') ||
              (a.personName || '').localeCompare(b.personName || ''),
          )

        return {
          ...reading,
          liveParticipants,
          signedUpReaders: signedUpByReadingId[reading.id] || [],
        }
      }),
    [
      readings,
      callStatesByReadingId,
      presenceByReadingId,
      signedUpByReadingId,
      now,
    ],
  )

  return {
    readings: monitoredReadings,
    loading: readingsLoading || callStatesLoading || presenceLoading,
    error: readingsError,
  }
}
